/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';

import { questionnaireVersion } from '@/data/matchPoliticoMethodology';
import { toPoliticalMatchCandidateMatches } from '@/adapters/political-match-adapter';
import { getPoliticalMatchQuestions, getPoliticalMatches, politicalMatchQuestionFallbacks } from '@/services/political-match-service';
import { getFirebaseEnvConfig } from '@/lib/firebase/config';
import {
  clearPoliticalMatchLocalState,
  loadPoliticalMatchLocalState,
  savePoliticalMatchLocalState,
} from '@/services/political-match-storage';
import { useBrowserQueryCache } from '@/lib/query-cache/use-browser-query-cache';
import type {
  PoliticalMatchAnswerValue,
  PoliticalMatchLocalState,
  PoliticalMatchMode,
  PoliticalMatchQuestion,
  PoliticalMatchPhase,
} from '@/models/political-match';
import {
  buildPoliticalMatchQuestionPlan,
  buildPoliticalMatchSummary,
} from '@/utils/political-match';

type PoliticalMatchFlowStatus = 'loading' | 'ready' | 'error';

type PoliticalMatchFlowAction =
  | { type: 'hydrate'; state: PoliticalMatchLocalState }
  | { type: 'selectMode'; mode: Exclude<PoliticalMatchMode, null> }
  | { type: 'recordAnswer'; questionId: string; answer: Exclude<PoliticalMatchAnswerValue, null> }
  | { type: 'clearAnswer'; questionId: string }
  | { type: 'advanceQuestion'; totalQuestions: number }
  | { type: 'goBackQuestion' }
  | { type: 'setQuestionIndex'; index: number }
  | { type: 'setPhase'; phase: PoliticalMatchPhase }
  | { type: 'setEmailAddress'; emailAddress: string }
  | { type: 'setEmailUnlocked'; emailUnlocked: boolean }
  | { type: 'completeQuestionnaire'; questions: PoliticalMatchQuestion[] }
  | { type: 'reset' };

type PoliticalMatchFlowContextValue = {
  status: PoliticalMatchFlowStatus;
  error?: string;
  questionnaireVersion: string;
  questions: PoliticalMatchQuestion[];
  questionPlan: PoliticalMatchQuestion[];
  currentQuestion: PoliticalMatchQuestion | null;
  totalQuestions: number;
  currentQuestionNumber: number;
  answeredQuestionCount: number;
  state: PoliticalMatchLocalState;
  selectMode: (mode: Exclude<PoliticalMatchMode, null>) => void;
  recordAnswer: (questionId: string, answer: Exclude<PoliticalMatchAnswerValue, null>) => void;
  clearAnswer: (questionId: string) => void;
  advanceQuestion: (totalQuestions: number) => void;
  goBackQuestion: () => void;
  setQuestionIndex: (index: number) => void;
  setPhase: (phase: PoliticalMatchPhase) => void;
  setEmailAddress: (emailAddress: string) => void;
  setEmailUnlocked: (emailUnlocked: boolean) => void;
  completeQuestionnaire: () => void;
  restartFlow: () => void;
};

const initialState: PoliticalMatchLocalState = {
  questionnaireVersion,
  mode: null,
  phase: 'setup',
  answers: {},
  emailAddress: '',
  emailUnlocked: false,
  currentQuestionIndex: 0,
};

function reducer(state: PoliticalMatchLocalState, action: PoliticalMatchFlowAction): PoliticalMatchLocalState {
  switch (action.type) {
    case 'hydrate':
      return {
        ...initialState,
        ...action.state,
        questionnaireVersion,
        phase: action.state.phase ?? 'setup',
        answers: action.state.answers ?? {},
        emailAddress: action.state.emailAddress ?? '',
        currentQuestionIndex: action.state.currentQuestionIndex ?? 0,
      };
    case 'selectMode':
      return {
        ...initialState,
        questionnaireVersion,
        mode: action.mode,
        phase: 'questions',
      };
    case 'recordAnswer':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: action.answer,
        },
      };
    case 'clearAnswer':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: null,
        },
      };
    case 'advanceQuestion':
      return {
        ...state,
        currentQuestionIndex: Math.min(state.currentQuestionIndex + 1, action.totalQuestions),
      };
    case 'goBackQuestion':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1),
      };
    case 'setQuestionIndex':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, action.index),
      };
    case 'setPhase':
      return {
        ...state,
        phase: action.phase,
      };
    case 'setEmailAddress':
      return {
        ...state,
        emailAddress: action.emailAddress,
      };
    case 'setEmailUnlocked':
      return {
        ...state,
        emailUnlocked: action.emailUnlocked,
      };
    case 'completeQuestionnaire': {
      const summary = buildPoliticalMatchSummary(action.questions, state.answers);

      return {
        ...state,
        currentQuestionIndex: action.questions.length,
        completedAt: new Date().toISOString(),
        economicScore: summary.economicScore,
        personalScore: summary.personalScore,
        phase: 'processing',
      };
    }
    case 'reset':
      return {
        ...initialState,
        matches: state.matches,
      };
    default:
      return state;
  }
}

const PoliticalMatchFlowContext = createContext<PoliticalMatchFlowContextValue | undefined>(undefined);

export function PoliticalMatchFlowProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryCache = useBrowserQueryCache();
  const [questions, setQuestions] = useState<PoliticalMatchQuestion[]>([]);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [status, setStatus] = useState<PoliticalMatchFlowStatus>('loading');
  const [error, setError] = useState<string | undefined>();
  const hydratedRef = useRef(false);

  useEffect(() => {
    let active = true;

    setStatus('loading');
    setError(undefined);

    void Promise.allSettled([
      getPoliticalMatchQuestions({ cache: queryCache }),
      loadPoliticalMatchLocalState(questionnaireVersion),
      getPoliticalMatches({ cache: queryCache }),
    ]).then(([questionsResult, savedStateResult, matchesResult]) => {
      if (!active) {
        return;
      }

      const loadedQuestions = questionsResult.status === 'fulfilled' ? questionsResult.value : politicalMatchQuestionFallbacks;
      const savedState = savedStateResult.status === 'fulfilled' ? savedStateResult.value : null;
      const useEmulators = getFirebaseEnvConfig().useEmulators;
      let loadedMatches = savedState?.matches;

      if ((loadedMatches?.length ?? 0) === 0 || !useEmulators) {
        if (matchesResult.status === 'fulfilled') {
          try {
            loadedMatches = toPoliticalMatchCandidateMatches(matchesResult.value);
          } catch {
            loadedMatches = savedState?.matches;
          }
        }
      }

      setQuestions(loadedQuestions);

      dispatch({
        type: 'hydrate',
        state: {
          questionnaireVersion,
          mode: savedState?.mode ?? null,
          phase: savedState?.phase === 'topics' ? 'questions' : savedState?.phase ?? 'setup',
          answers: savedState?.answers ?? {},
          emailAddress: savedState?.emailAddress ?? '',
          emailUnlocked: savedState?.emailUnlocked ?? false,
          currentQuestionIndex: savedState?.currentQuestionIndex ?? 0,
          economicScore: savedState?.economicScore,
          personalScore: savedState?.personalScore,
          completedAt: savedState?.completedAt,
          matches: loadedMatches,
        },
      });

      hydratedRef.current = true;
      setStatus('ready');
    });

    return () => {
      active = false;
    };
  }, [queryCache]);

  useEffect(() => {
    if (!hydratedRef.current || status !== 'ready') {
      return;
    }

    void savePoliticalMatchLocalState(state);
  }, [state, status]);

  const questionPlan = useMemo(() => buildPoliticalMatchQuestionPlan(questions, state.mode), [questions, state.mode]);
  const totalQuestions = questionPlan.length;
  const currentQuestion = questionPlan[state.currentQuestionIndex] ?? null;
  const currentQuestionNumber = Math.min(state.currentQuestionIndex + 1, totalQuestions || 1);
  const answeredQuestionCount = useMemo(
    () => Object.values(state.answers).filter((answer) => answer !== null && answer !== undefined).length,
    [state.answers],
  );

  const value = useMemo<PoliticalMatchFlowContextValue>(
    () => ({
      status,
      error,
      questionnaireVersion,
      questions,
      questionPlan,
      currentQuestion,
      totalQuestions,
      currentQuestionNumber,
      answeredQuestionCount,
      state,
      selectMode: (mode) => dispatch({ type: 'selectMode', mode }),
      recordAnswer: (questionId, answer) => dispatch({ type: 'recordAnswer', questionId, answer }),
      clearAnswer: (questionId) => dispatch({ type: 'clearAnswer', questionId }),
      advanceQuestion: (count) => dispatch({ type: 'advanceQuestion', totalQuestions: count }),
      goBackQuestion: () => dispatch({ type: 'goBackQuestion' }),
      setQuestionIndex: (index) => dispatch({ type: 'setQuestionIndex', index }),
      setPhase: (phase) => dispatch({ type: 'setPhase', phase }),
      setEmailAddress: (emailAddress) => dispatch({ type: 'setEmailAddress', emailAddress }),
      setEmailUnlocked: (emailUnlocked) => dispatch({ type: 'setEmailUnlocked', emailUnlocked }),
      completeQuestionnaire: () => dispatch({ type: 'completeQuestionnaire', questions: questionPlan }),
      restartFlow: () => {
        hydratedRef.current = false;
        dispatch({ type: 'reset' });
        void clearPoliticalMatchLocalState(questionnaireVersion).finally(() => {
          hydratedRef.current = true;
        });
      },
    }),
    [answeredQuestionCount, currentQuestion, error, questionPlan, questions, state, status, totalQuestions, currentQuestionNumber],
  );

  return <PoliticalMatchFlowContext.Provider value={value}>{children}</PoliticalMatchFlowContext.Provider>;
}

export function usePoliticalMatchFlow(): PoliticalMatchFlowContextValue {
  const context = useContext(PoliticalMatchFlowContext);

  if (!context) {
    throw new Error('usePoliticalMatchFlow deve ser usado dentro de PoliticalMatchFlowProvider.');
  }

  return context;
}
